import React, { useState } from 'react';
import { Button, Divider, Box } from '@mui/material';
import { useSelector } from 'react-redux';
import {
	activeShopPublicInfo,
	getShopCurrency,
	activeShopDefaultVat,
} from 'selectors/ShopSelectors';
import { OrderObject, OrderPaymentRequestData, PaymentRequest } from 'common/types';
import PaymentDialog from '../../PaymentOverview';
import {
	createPaymentObjects,
	getItemsForPayment,
	getItemsForDeposit,
} from 'common/utils/payments';
import { saveOrderPaymentInfo, saveOrderDepositInfo } from 'common/api/frontend/payments';
import { newFirestoreId } from 'common/utils/newRentalUtils';
import { getShopReceiptInfo } from 'common/utils/shopUtils';
import PayDialog from '../../PayDialog';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import ChargeSection from './ChargeSection';
import DepositSection from './DepositSection';
import { hasDeposit } from 'common/modules/atoms/deposit';
import { useTranslation } from 'services/localization/useTranslation';
import { getNewPaymentNumber } from 'common/api/frontend';
import {
	getItemsPayments,
	getOrderPayments,
	getOrderDeliveryForPayment,
} from 'common/modules/atoms/paymentAmounts';

interface Props {
	rental: OrderObject;
}

export const PaymentSection = (props: Props) => {
	const { rental } = props;
	const { rentalInfo, products, shoppers, orderDelivery } = rental;
	const pricing = rentalInfo.pricing;
	const shopPublicInfo = useSelector(activeShopPublicInfo)!;
	const shopCurrency = useSelector(getShopCurrency);
	const defaultVat = useSelector(activeShopDefaultVat);
	const [showPaymentDialog, setShowPaymentDialog] = useState<boolean>(false);
	const [showPayDialog, setShowPayDialog] = useState<boolean>(false);

	const { t } = useTranslation();
	const currency = getCurrencyObjectFromCurrencyCode(pricing.currency, shopCurrency);

	const markAsPaid = async () => {
		const paymentAmounts = getOrderPayments(rentalInfo).charge;
		const unpaid = paymentAmounts.unpaid;
		const amount = {
			value: unpaid,
			currency: rentalInfo.pricing.currency,
		};
		const paymentNumber = await getNewPaymentNumber(rentalInfo.shopId);
		const orderPaymentRequestId = newFirestoreId();
		const productsForPayment = products.filter((p) => {
			const productChargePayment = getItemsPayments(p).charge;
			return productChargePayment.unpaid > 0 || productChargePayment.total === 0;
		});

		const orderDeliveryForPayment = getOrderDeliveryForPayment(orderDelivery);

		const paymentRequest: PaymentRequest = {
			items: getItemsForPayment(productsForPayment, orderDeliveryForPayment, amount, t),
			productIds: productsForPayment.map((p) => p.id),
			orderId: rentalInfo.id,
			shopId: rentalInfo.shopId,
			amount,
			orderPaymentRequestId,
			live: rentalInfo.live,
			locationId: rentalInfo.startLocation.id,
			shopperId:
				(!rentalInfo.responsiblePerson.external && rentalInfo.responsiblePerson.shopperId) || null,
			channel: 'ADMIN',
			transactionsInfo: [
				{
					transactionId: newFirestoreId(),
					method: 'MANUAL',
					pspReference: null,
					provider: 'MANUAL',
					amount,
					splitData: null,
				},
			],
			startDate: rentalInfo.startDate,
			receiptNumber: -1,
			shopReceiptInfo: getShopReceiptInfo(shopPublicInfo, rentalInfo.startLocation.id),
			paymentType: 'CHARGE',
			capture: true,
			paymentAction: 'PAYMENT',
		};
		const payments = createPaymentObjects(paymentRequest);
		const orderPaymentRequest: OrderPaymentRequestData = {
			id: orderPaymentRequestId,
			shopId: rentalInfo.shopId,
			orderId: rentalInfo.id,
			amount: paymentRequest.amount,
			paymentNumber,
			shopperIds: [...new Set(productsForPayment.map((p) => p.shopperId))],
			productIds: productsForPayment.map((p) => p.id),
		};
		await saveOrderPaymentInfo(payments, orderPaymentRequest);
	};

	const markAsReserved = async () => {
		const depositPayment = getOrderPayments(rentalInfo).deposit;
		const unreserved = depositPayment.unreserved;
		const amount = {
			value: unreserved,
			currency: depositPayment.currency,
		};
		const paymentRequest: PaymentRequest = {
			items: getItemsForDeposit(amount, defaultVat, rentalInfo.pricing.taxExcluded),
			productIds: [],
			orderId: rentalInfo.id,
			shopId: rentalInfo.shopId,
			amount,
			orderPaymentRequestId: null,
			live: rentalInfo.live,
			locationId: rentalInfo.startLocation.id,
			shopperId:
				(!rentalInfo.responsiblePerson.external && rentalInfo.responsiblePerson.shopperId) || null,
			channel: 'ADMIN',
			transactionsInfo: [
				{
					transactionId: newFirestoreId(),
					method: 'MANUAL',
					pspReference: null,
					provider: 'MANUAL',
					amount,
					splitData: null,
				},
			],
			startDate: rentalInfo.startDate,
			receiptNumber: -1,
			shopReceiptInfo: getShopReceiptInfo(shopPublicInfo, rentalInfo.startLocation.id),
			paymentType: 'DEPOSIT',
			capture: false,
			paymentAction: 'AUTHORISATION',
		};
		const payments = createPaymentObjects(paymentRequest);
		const orderDepositRequest: OrderPaymentRequestData = {
			id: null,
			shopId: rentalInfo.shopId,
			orderId: rentalInfo.id,
			amount: paymentRequest.amount,
			paymentNumber: null,
			shopperIds: shoppers.map((s) => s.id),
			productIds: products.map((p) => p.id),
		};
		await saveOrderDepositInfo(payments, orderDepositRequest);
	};

	return (
		<Box py={1}>
			<ChargeSection
				total={pricing.total}
				payment={rentalInfo.charge}
				additionalPricingInfo={pricing.additionalInfo}
				currency={currency}
				markAsPaid={markAsPaid}
				showPayDialog={() => setShowPayDialog(true)}
				order={rental}
			/>
			{hasDeposit(pricing.deposit) && (
				<>
					<Box pt={1} pb={1}>
						<Divider />
					</Box>
					<DepositSection
						deposit={pricing.deposit}
						payment={rentalInfo.deposit}
						currency={currency}
						markAsReserved={markAsReserved}
						showPayDialog={() => null}
					/>
				</>
			)}
			<Button
				fullWidth
				variant="text"
				color="secondary"
				size="small"
				onClick={() => setShowPaymentDialog(true)}
			>
				{t('payment.summary', 'Payment summary')}
			</Button>
			{showPayDialog && (
				<PayDialog
					open={showPayDialog}
					order={rental}
					handleClose={() => setShowPayDialog(false)}
				/>
			)}
			{showPaymentDialog && (
				<PaymentDialog
					open={showPaymentDialog}
					rental={rental}
					handleClose={() => setShowPaymentDialog(false)}
					currency={currency}
				/>
			)}
		</Box>
	);
};

export default PaymentSection;
